import { useState } from 'react';
import {
  MDSButton,
  MDSDivider,
  MDSDropdown,
  MDSIcon,
  MDSModal,
  MDSPlainButton,
  MDSTypography,
} from '@marqvision/mds-v2';
import { Comment, AppInfo } from '../../../@types';
import { Styled } from '../../styles';
import { downloadFileWithFilename } from '../../../../../utils';
import { getDateString, getRepliesLabel } from '../../@utils';
import { getIcon } from '../../../@utils';
import marqvisionIcon from './marqvision-icon.png';

type Props = {
  comment: Comment;
  appInfo: AppInfo;
  isThread?: boolean;
  onOpenThread?: (selectedComment: Comment) => void;
  onDelete?: (messageId: number) => void;
};
export const MessageItem = (props: Props) => {
  const { comment, isThread, onOpenThread, onDelete, appInfo } = props;
  const { id, user, text, createdAt, replyCount, files, deleted, pendingBy } = comment;
  const formattedCreatedAt = getDateString(createdAt);

  const initial = user.email.slice(0, 2).toUpperCase();
  const repliesLabel = getRepliesLabel(replyCount ?? 0);

  const handleOpenThread = () => {
    onOpenThread?.({
      id,
      user,
      text,
      createdAt: formattedCreatedAt,
      replyCount,
      files,
      deleted,
      type: 'comment',
    });
  };

  const [isOpen, setIsOpen] = useState(false);
  const handleDeleteMenuClick = () => {
    setIsOpen(true);
  };
  const handleDeleteConfirm = () => {
    setIsOpen(false);
    onDelete?.(id);
  };

  const showAsDeleted = deleted || pendingBy === 'fetching-after-delete';

  return (
    <>
      <Styled.CommentBox isPending={pendingBy === 'adding' || pendingBy === 'deleting'}>
        <Styled.UserInfo isAdmin={user.isAdmin} isDeleted={showAsDeleted}>
          <div className="initial">
            {showAsDeleted ? (
              <MDSIcon.Trash variant="outline" color="color/content/neutral/default/disabled" />
            ) : appInfo.useMarqvisionIcon && user.isAdmin ? (
              <img src={marqvisionIcon} alt="marqvision admin" />
            ) : (
              <MDSTypography variant="title" size="l" weight="medium" as="p">
                {initial}
              </MDSTypography>
            )}
          </div>
          {showAsDeleted ? (
            <MDSTypography variant="body" size="m" color="color/content/neutral/secondary/normal">
              This comment was deleted.
            </MDSTypography>
          ) : (
            <div className="email">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {pendingBy === 'adding' ? (
                  <Styled.Skeleton />
                ) : (
                  <MDSTypography variant="body" size="m" weight="medium">
                    {appInfo.useMarqvisionIcon && user.isAdmin ? 'Marqvision' : user.email}
                  </MDSTypography>
                )}

                {/* todo-@jamie: 지울 권한이 있는 유저인지도 체크 필요 */}
                {isMessageRemovable(comment, appInfo) && onDelete && (
                  <MDSDropdown
                    width="200px"
                    list={[
                      {
                        label: (
                          <MDSTypography
                            variant="body"
                            size="m"
                            color="color/bg/fill/critical/default/normal"
                            as="span"
                          >
                            Delete
                          </MDSTypography>
                        ),
                        onClick: handleDeleteMenuClick,
                      },
                    ]}
                    renderAnchor={() => (
                      <MDSPlainButton color="bluegray" icon={<MDSIcon.More variant="horizontal" />} />
                    )}
                  />
                )}
              </div>
              <div>
                <MDSTypography variant="body" size="s" color="color/content/neutral/secondary/normal">
                  {formattedCreatedAt}
                </MDSTypography>
              </div>
            </div>
          )}
        </Styled.UserInfo>

        {!showAsDeleted && (
          <>
            <MDSTypography whiteSpace="pre-wrap" wordBreak="break-word" style={{ marginLeft: '50px' }}>
              {text}
            </MDSTypography>

            {files.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '4px',
                  marginBottom: '2px',
                  marginLeft: '50px',
                }}
              >
                {files.map((file) => (
                  <MDSButton
                    key={file.id}
                    variant="border"
                    color="bluegray"
                    size="small"
                    startIcon={getIcon(file.name)}
                    onClick={() => {
                      try {
                        downloadFileWithFilename(file.url, file.name);
                      } catch (error) {
                        console.error(error);
                      }
                    }}
                  >
                    {file.name}
                  </MDSButton>
                ))}
              </div>
            )}
          </>
        )}

        {!isThread && (
          <Styled.ReplyContainer>
            <Styled.ReplyPreview>
              <MDSPlainButton
                size="small"
                startIcon={appInfo.isReadOnly ? undefined : <MDSIcon.Reply />}
                onClick={handleOpenThread}
              >
                {!appInfo.isReadOnly && (
                  <>
                    Reply
                    {repliesLabel !== '' && (
                      <MDSDivider variant="dot" thickness={3} orientation="vertical" style={{ margin: '0 4px' }} />
                    )}
                  </>
                )}
                {repliesLabel}
              </MDSPlainButton>
            </Styled.ReplyPreview>
          </Styled.ReplyContainer>
        )}
      </Styled.CommentBox>

      <MDSModal.Wrapper width="420px" isOpen={isOpen}>
        <MDSModal.Content>
          <MDSTypography variant="body" size="l">
            Are you sure you want to delete this comment?
            <br /> This action cannot be undone.
          </MDSTypography>
        </MDSModal.Content>
        <MDSModal.Action>
          <MDSButton variant="border" size="medium" color="bluegray" onClick={() => setIsOpen(false)}>
            Cancel
          </MDSButton>
          <MDSButton variant="fill" size="medium" color="red" onClick={handleDeleteConfirm}>
            Delete
          </MDSButton>
        </MDSModal.Action>
      </MDSModal.Wrapper>
    </>
  );
};

const isMessageRemovable = (comment: Comment, appInfo: AppInfo) => {
  if (appInfo.isReadOnly) {
    return false;
  }
  if (appInfo.isWriterAdmin) {
    // admin > client view로 접속한 admin 유저
    return comment.user.isAdmin; // admin 유저가 작성한 코멘트만 삭제할 수 있다.
  } else {
    // client view로 접속한 client 유저
    return comment.user.email === appInfo.writerEmail;
  }
};
